'use client';
import { useCallback, useMemo } from 'react';
import type { EligibilityDetails } from '../../types/eligibility';
import {
  CITIZENSHIP_QUERY_PARAM,
  ELIGIBILITY_QUERY_PARAMS,
  INCOME_QUERY_PARAM,
  readEligibilityDetails,
} from './eligibilityQueryParams';
import { usePanelQueryParams } from './usePanelQueryParams';

/** A stable identity, so a page with nothing entered does not re-memoise. */
const NO_DETAILS: EligibilityDetails = {};

export type UseProductEligibilityResult = {
  details: EligibilityDetails;
  setCitizenship: (value: string | undefined) => void;
  setMonthlyIncome: (value: number | undefined) => void;
  /** Drops both parameters in one write, for Clear All (AC 1f / 1g). */
  clearDetails: () => void;
};

/**
 * The reader's details, derived from the URL rather than held in state — the
 * same reason the sort and the filters are: the panel, the table and the cards
 * cannot drift, and a deep link takes the same path as a click.
 */
export const useProductEligibility = (
  offeredCitizenships: readonly string[],
): UseProductEligibilityResult => {
  const { readParam, paramsKey, write } = usePanelQueryParams(
    ELIGIBILITY_QUERY_PARAMS,
  );

  const details = useMemo(
    () =>
      paramsKey === null
        ? NO_DETAILS
        : readEligibilityDetails(offeredCitizenships, readParam),
    // `readParam` is stable per search-params identity, which `paramsKey`
    // already tracks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [offeredCitizenships, paramsKey],
  );

  // Dropped rather than left empty, so a cleared parameter is
  // indistinguishable from one the reader never touched.
  const setParam = useCallback(
    (name: string, value: string | undefined) =>
      write((params) => {
        if (value) params.set(name, value);
        else params.delete(name);
      }),
    [write],
  );

  const setCitizenship = useCallback(
    (value: string | undefined) => setParam(CITIZENSHIP_QUERY_PARAM, value),
    [setParam],
  );

  const setMonthlyIncome = useCallback(
    (value: number | undefined) =>
      setParam(
        INCOME_QUERY_PARAM,
        value && value > 0 ? String(value) : undefined,
      ),
    [setParam],
  );

  const clearDetails = useCallback(
    () =>
      write((params) =>
        ELIGIBILITY_QUERY_PARAMS.forEach((name) => params.delete(name)),
      ),
    [write],
  );

  return { details, setCitizenship, setMonthlyIncome, clearDetails };
};
